import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import usePageMeta from "@/hooks/usePageMeta";
import { allMovies, filterMovies, DEFAULT_FILTER, sortMovies } from "@/lib/movies";

const typeOptions = ["全部", "电影", "剧集"];
const regionOptions = ["全部", "国产", "港台", "海外"];
const hotKeywords = ["悬疑", "家庭", "科幻", "犯罪", "治愈", "历史"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const type = searchParams.get("type") ?? "全部";
  const region = searchParams.get("region") ?? "全部";
  const [inputValue, setInputValue] = useState(query);

  useEffect(() => {
    setInputValue(query);
  }, [query]);

  const results = filterMovies(
    { ...DEFAULT_FILTER, keyword: query, type, region },
    sortMovies(allMovies, "default"),
  );

  const hotMovies = [...allMovies].sort((a, b) => b.views - a.views).slice(0, 3);

  usePageMeta(
    query ? `“${query}”的搜索结果 - 淫乱小镇` : "搜索影视作品 - 淫乱小镇",
    query
      ? `在淫乱小镇搜索“${query}”，共找到 ${results.length} 部相关影视作品，包含影片简介、剧情解析与分集内容。`
      : "在淫乱小镇按关键词搜索影视作品，支持片名、演员、导演、题材与剧情内容检索，快速定位你想了解的剧集与电影。",
    "淫乱小镇,影视搜索,剧情介绍,影视分析,影视作品",
  );

  const applyKeyword = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value.trim()) {
      next.set("q", value.trim());
    } else {
      next.delete("q");
    }
    setSearchParams(next, { replace: true });
  };

  const updateFilter = (key: "type" | "region", value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "全部") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    applyKeyword(inputValue);
  };

  const handleChange = (value: string) => {
    setInputValue(value);
    applyKeyword(value);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-16 md:pt-20">
        <section className="border-b border-background-200/70 bg-background-100/40">
          <div className="max-w-shell mx-auto px-4 md:px-8 py-8 md:py-10">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-xs text-foreground-600">
              <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <span className="text-foreground-300">搜索结果</span>
            </nav>

            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-50">
              搜索影视作品
            </h1>
            <p className="mt-2.5 text-sm text-foreground-500">
              支持按片名、导演、演员、题材与剧情内容检索，输入即筛选。
            </p>

            <form onSubmit={handleSubmit} className="mt-5 flex flex-col sm:flex-row gap-2.5">
              <div className="relative flex-1 min-w-0">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500"></i>
                <input
                  value={inputValue}
                  onChange={(event) => handleChange(event.target.value)}
                  type="text"
                  name="q"
                  placeholder="输入片名、导演、演员或题材关键词"
                  aria-label="搜索关键词"
                  className="w-full h-12 pl-11 pr-4 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/40 transition-colors box-border"
                />
              </div>
              <button
                type="submit"
                className="h-12 px-7 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>

            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-foreground-600">热门关键词</span>
              {hotKeywords.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => {
                    setInputValue(word);
                    applyKeyword(word);
                  }}
                  className="px-2.5 py-1 rounded-full bg-background-200/60 text-foreground-400 hover:bg-primary-500 hover:text-foreground-50 transition-colors cursor-pointer"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-shell mx-auto px-4 md:px-8 py-8 md:py-10">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <p className="text-sm text-foreground-400">
              {query ? (
                <>
                  “<span className="text-accent-500">{query}</span>” 共找到{" "}
                  <span className="font-label text-foreground-100">{results.length}</span> 部作品
                </>
              ) : (
                <>
                  全部作品 <span className="font-label text-foreground-100">{results.length}</span> 部
                </>
              )}
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-foreground-600 mr-0.5">类型</span>
                {typeOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => updateFilter("type", option)}
                    className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      type === option
                        ? "bg-primary-500 text-foreground-50"
                        : "bg-background-200/60 text-foreground-400 hover:text-foreground-100"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
              <span className="hidden sm:block w-px h-4 bg-background-300"></span>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-foreground-600 mr-0.5">地区</span>
                {regionOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => updateFilter("region", option)}
                    className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      region === option
                        ? "bg-primary-500 text-foreground-50"
                        : "bg-background-200/60 text-foreground-400 hover:text-foreground-100"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {results.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {results.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="mt-6">
              <EmptyState
                icon="ri-search-eye-line"
                title={query ? `没有找到与“${query}”相关的作品` : "还没有输入搜索关键词"}
                description="试试换一个更短的关键词，比如只输入片名中的两个字；也可以清除类型与地区筛选，或者从下方热门内容里挑一部看看。"
                actionLabel="清空搜索与筛选"
                onAction={() => {
                  setInputValue("");
                  setSearchParams(new URLSearchParams(), { replace: true });
                }}
              />

              <div className="mt-10">
                <div className="flex items-center gap-2 mb-5">
                  <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-50">
                    热门内容推荐
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {hotMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}